import React, { useRef, useState } from 'react';
import Icon from '@/components/atoms/UI/Icon/Icon';
import IconButton from '@/components/atoms/UI/buttons/IconButton';
import styled from 'styled-components';
import { primitiveColors, radius, Spin } from '@/styles/template';
import { TextXS } from '@/components/atoms/UI/Typography';
import { useTranslation } from 'react-i18next';

interface Props {
	children: React.ReactNode
}



// code {
// 	width: 100%;
// 	overflow: auto;
// }

/**
 * Wrap — флекс-контейнер «текст + кнопка копирования». Сам НЕ скроллится:
 * `overflow: visible`, чтобы `:focus-visible` ring у `IconButton`
 * (`focusRing` = 2px outline + 2px offset, см. `styles/template.ts`)
 * не обрезался по вертикали при Tab-навигации. Горизонтальный скролл
 * длинного сниппета живёт ниже — на `SideToCopy`.
 *
 * Размечен `data-no-swipe` + `touch-action: pan-x`, чтобы корректно
 * сосуществовать со swipe-to-delete родителя (см. `RemovableItem`):
 * палец на этой зоне скроллит код, а не пытается удалить карточку.
 * Контракт описан в JSDoc `removableRootStyles` в `removable.shared.ts`.
 */
const Wrap = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
  width: 100%;
  touch-action: pan-x;
  box-shadow: inset 0 0 0 2px ${(props) => props.theme.semantic.surface.secondary};
  border-radius: ${(p) => p.theme.primitives?.radius?.sm ?? radius.normal};
  padding-left: 2rem;
`;

/**
 * Реальный горизонтальный скролл длинного сниппета. `min-width: 0`
 * критичен: иначе flex-item с дефолтным `min-width: auto` распирается
 * под контент и собственный `overflow-x: auto` не срабатывает —
 * скроллить начинает родительский `Wrap`, а его `overflow: hidden`
 * обрезает focus-ring у соседней кнопки копирования.
 */
const SideToCopy = styled.div`
  color: ${(props) => props.theme.semantic.text.primary};
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  /* Firefox не понимает ::-webkit-scrollbar из global.ts — задаём кастомный
     тонкий скролл его свойствами (тот же цвет thumb, прозрачный трек). */
  scrollbar-width: thin;
  scrollbar-color: ${primitiveColors.gray[400]} transparent;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-right: 1rem;
  position: relative;
  text-align: left;
  height: 100%;
  display: flex;
  align-items: center;
`;

const IconButtonCopy = styled(IconButton)`
	flex-shrink: 0;
	&.animate svg {
		-webkit-animation: ${Spin} 600ms;
		animation: ${Spin} 600ms;
	}
`;

const CopyTextInside: React.FC<Props> = ({ children }) => {
	const [isCopied, setIsCopied] = useState(false);
	const textRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const text_copied = t('copied');

	const handleCopy = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		if (textRef.current) {
			const plainText = textRef.current.innerText;

			navigator.clipboard.writeText(plainText)
			.then(() => {
				setIsCopied(true);

				setTimeout(() => {
					setIsCopied(false);
				}, 1000);
			})
			.catch((err) => {
				console.error('Failed to copy text: ', err);
			});
		}
	};

	return (
		<Wrap data-no-swipe>
			<SideToCopy ref={textRef}>{isCopied ? <TextXS>{text_copied}</TextXS> : children}</SideToCopy>
			<IconButtonCopy large primary success={isCopied} onClick={handleCopy} className={`${isCopied && 'animate'}`}>
				{isCopied ? <Icon name={'Check'} xSmall /> : <Icon name={'Copy'} small />}
			</IconButtonCopy>
		</Wrap>
	);
};

export default CopyTextInside;